import { HttpClient, HttpErrorResponse, HttpResponse, HttpParams } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { BehaviorSubject, Subject, Observable, catchError, map, of, tap } from "rxjs";
import { environment } from '../../environments/environment';
import { UserModel,  JwTokenModel, ArkUserType } from "../core/api/models";
import { AccountApiService  } from '../core/api/services';
import { Utils } from "../core/utils";

@Injectable({
  providedIn: 'root',
})
export class AuthService {

  accountApiService = inject(AccountApiService);

  public tokenInfo$: Subject<JwTokenModel | null> = new Subject<JwTokenModel | null>();
  public tokenInfo: JwTokenModel | null = null;

  
  public userInfo$: Subject<UserModel | null> = new Subject<UserModel | null>();
  public userInfo: UserModel | null = null;

  public latitude?: number;
  public longitude?: number;
  public deviceName?: string;


  private _authStateChanged: Subject<boolean> = new BehaviorSubject<boolean>(false);

  public onStateChanged() {
    return this._authStateChanged.asObservable();
  }
  
  constructor(private http: HttpClient, private router: Router,
    private route: ActivatedRoute) {

    const tokenInfoData = localStorage.getItem('tokenInfo');
    if (tokenInfoData) {
      this.tokenInfo = JSON.parse(tokenInfoData) as JwTokenModel;

      if (this.tokenInfo.expires_at != null
        && this.tokenInfo.access_token != null
        && new Date(this.tokenInfo.expires_at) > new Date())
        console.info('Has Valid Access Token', Utils.parseJwt(this.tokenInfo.access_token  ));
      else if (this.tokenInfo.refresh_expires_at != null
        && new Date(this.tokenInfo.refresh_expires_at) > new Date())
        console.info('Has Valid Refresh Token Info', this.tokenInfo.refresh_token);
      else
        console.warn('Has Expired Token Info', this.tokenInfo.refresh_expires_at);

      this.tokenInfo$.next(this.tokenInfo);
    } else {
      this.tokenInfo$.next(null);
    } 

    const currentUserData = localStorage.getItem('userInfo');
    if (currentUserData) {
      this.userInfo = JSON.parse(currentUserData) as UserModel;
      console.debug('User Info', this.userInfo);
      this.userInfo$.next(this.userInfo);
    } else {
      this.userInfo$.next(null);
    }

    this.tokenInfo$.subscribe(x => {
      console.debug('tokenInfo$', x);
      if (x) {
        localStorage.setItem('tokenInfo', JSON.stringify(x));
        // save to cookie OptoJWT
        //if (!environment.production)
        document.cookie = `OptoJWT=${x.access_token}; path=/;`;
      }
      else
        localStorage.removeItem('tokenInfo');
        this.tokenInfo = x;
    });

    this.userInfo$.subscribe(x => {
      console.debug('userInfo$', x);
      if (x) {
        localStorage.setItem('userInfo', JSON.stringify(x));
      }
      else
        localStorage.removeItem('userInfo');
      this.userInfo = x;
    });


    this.getPosition();
    this.getDeviceName();
  }


  public signIn(username: string, password: string) {


    return this.accountApiService.Authenticate({ body: { username: username, password: password, latitude: this.latitude, longitude: this.longitude, deviceName: this.deviceName } })
      .pipe(tap(token => {
        if (token && token.access_token) {
          this.tokenInfo$.next(token);
          this._authStateChanged.next(true);
        } else {
          this.tokenInfo$.next(null);
          this._authStateChanged.next(false);
        }
        return ;
      },
        error => {
          console.log(error);
          //this.messageService.add({ severity: 'error', detail: 'Internal Error: ' + (error.message || error) });
        }));
  }



  // sign out
  public signOut() {


    this._authStateChanged.next(false);
    const currenttokenInfo = JSON.parse(localStorage.getItem('tokenInfo') || '{}');

    this.accountApiService.Logout()
      .forEach(() => { })
      .catch( err => { })
      .finally(() => { });

    this.tokenInfo$.next(null);
    this.userInfo$.next(null);
    this._authStateChanged.next(false);
   
    this.router.navigate(['/signin']);

    return of(true);
     
  }


  
  public isSignedIn(): boolean {


    if (this.tokenInfo != null
      && this.tokenInfo.expires_at != null
      && new Date(this.tokenInfo.expires_at) > new Date()) {
      return true;
    }
    return false;
  }


  public hasUserType(userType: ArkUserType): boolean
  {
    if (!this.userInfo
      || !this.userInfo.UserTypes)
      return false;
    return this.userInfo.UserTypes.indexOf(userType) >= 0;
  }
  public checkIfAutorized(userTypes: ArkUserType[]) {
    var check = (user: UserModel | null) => {

      if (this.tokenInfo && this.tokenInfo.refresh_token == null) {
        this.router.navigate(['/error'], { queryParams: { unauthorized: this.router.routerState.snapshot.url, missingTwoFactor: true } });
        return;
      }
      if (this.userInfo && this.userInfo.UserTypes && this.userInfo.UserTypes.length > 0) {
        for (var i = 0; i < userTypes.length; i++) {
          if (this.userInfo.UserTypes.indexOf(userTypes[i]) >= 0) {
            return ;
          }
        }
      }
      this.router.navigate(['/error'], { queryParams: { unauthorized: this.router.routerState.snapshot.url, userTypes: userTypes } });
      
    };
    if (this.userInfo)
      check(this.userInfo);
    else
      this.user().forEach(user => check(user));
  }


  public redirectAfterLogin(userInfo?: UserModel | null, returnUrl?: string | undefined | null) {
    userInfo = userInfo || this.userInfo;
    if (userInfo == null || userInfo == undefined) 
      this.router.navigate(['/signin']);
    else if (userInfo.UserTypes == null || !this.tokenInfo?.refresh_token)
      this.router.navigate(['/profile/'], {
        queryParams: {
          returnUrl: returnUrl,
          status: 'Verify2FA'
        }
      });
    else if (userInfo.PasswordExpireDate != null
      && new Date(userInfo.PasswordExpireDate) < new Date())
      this.router.navigate(['/profile/'],  {
        queryParams: {
          returnUrl: returnUrl,
          status:'PasswordExpired'
        }
      });
    else if (returnUrl && returnUrl != '' && returnUrl != '/' && returnUrl != '/signin')
      this.router.navigate([returnUrl]);
    //TODO: remove this and change to redirect to the home page
    else  {
      //check if there is any patient on the patientSession
      
      if (userInfo.UserTypes.indexOf(ArkUserType.Developer) >= 0)
        this.router.navigate(['/systemstatus']);
      else if (userInfo.UserTypes.indexOf(ArkUserType.Support) >= 0)
        this.router.navigate(['/tickets']);
      else if (userInfo.UserTypes.indexOf(ArkUserType.Admin) >= 0)
        this.router.navigate(['/clients']);
      else if (userInfo.UserTypes.indexOf(ArkUserType.Marketing) >= 0)
        this.router.navigate(['/potencial-clients']);
      else
        this.router.navigate(['/profile']);
    }
  }


  private _gettingTokenInfo = false;
  public getTokenInfo(): Observable<JwTokenModel | null> {

    this._gettingTokenInfo = true;
    var refresh_token: string | undefined = undefined;

    if (this.tokenInfo != null
      && this.tokenInfo.refresh_token != null
      && this.tokenInfo.refresh_expires_at != null
      && new Date(this.tokenInfo.refresh_expires_at) > new Date())
      refresh_token = this.tokenInfo.refresh_token!;


    console.debug('getTokenInfo', refresh_token);

    return this.accountApiService.RefreshToken({ body: { refresh_token: refresh_token, latitude: this.latitude, longitude: this.longitude, deviceName: this.deviceName } })

      .pipe(tap(
        (result) => {
          this._gettingTokenInfo = false;
          this.tokenInfo$.next(result);
        },
        (err) => {
          this._gettingTokenInfo = false;
          this.userInfo$.next(null);
          this.tokenInfo$.next(null);
          return of(null);
        }));
  }

  public token() {
    if (this.tokenInfo) {
      return of(this.tokenInfo);
    }
    if (this._gettingTokenInfo)
      return this.tokenInfo$.asObservable();

    return this.getTokenInfo().pipe(
      map((tokenInfo) => {
        if (tokenInfo) {
          return tokenInfo;
        }
        return null;
      }));
  }


  private _gettingUserInfo = false;
  public getUserInfo(): Observable<UserModel | null> {


    this._gettingUserInfo = true;
    return this.accountApiService.Me()

      .pipe(tap(
        (result) => {
          this._gettingUserInfo = false;
          this.userInfo$.next(result);
        },
        (err) => {
          this._gettingUserInfo = false;
          this.userInfo$.next(null);
          return of(null);
        }));
  }
  
  public user() {
    if (this.userInfo) {
      return of(this.userInfo);
    }
    if (this._gettingUserInfo)
      return this.userInfo$.asObservable();

    return this.getUserInfo().pipe(
      map((userInfo) => {
        if (userInfo) {
          return userInfo;
        }
        return null;
      }));
  }

  public getPosition() {
    try {
      if (this.longitude != null && this.latitude != null)
        return;
      if (!navigator
        || !navigator.geolocation
        || !navigator.geolocation.getCurrentPosition)
        return;

      navigator.geolocation.getCurrentPosition((position) => {
        this.longitude = position.coords.longitude;
        this.latitude = position.coords.latitude;
        console.debug(`${new Date(position.timestamp).toTimeString()} ${position.coords.latitude.toFixed(6)}, ${position.coords.longitude.toFixed(6)}`);

      }, (positionError) => {
        console.error(positionError);
      }, {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 10000 * 60 * 5
      });
    } catch (ex) {

    }

  }

  public getDeviceName() {

    // ua-parser-js
    /*
    var uap = new UAParser();
    var result = uap.getResult();
    if (result.device.model || result.device.vendor)
      self.DeviceName(`${result.device.vendor} ${result.device.model} ${result.os.name} ${result.os.version} ${result.browser.name} ${result.browser.version}`);
    else
      self.DeviceName(`${result.os.name} ${result.os.version} ${result.browser.name} ${result.browser.version}`);

    result = await uap.getResult().withClientHints();

    if (result.device.model || result.device.vendor)
      self.DeviceName(`${result.device.vendor} ${result.device.model} ${result.os.name} ${result.os.version} ${result.browser.name} ${result.browser.version}`);
    else
      self.DeviceName(`${result.os.name} ${result.os.version} ${result.browser.name} ${result.browser.version}`);
      */

    this.deviceName = navigator.userAgent;
    
  }

}
